import { useEffect, useMemo, useState } from "react";
import { Layers, Search, Loader2, ChevronLeft, AlertCircle, ArrowRight } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { Link } from "react-router-dom";

/**
 * Inline category editor for the inventory catalogue.
 *
 * The 230 items currently flagged "Other" are the main target — reps and
 * admins need a friction-less way to re-bucket them without leaving the
 * page.
 */
export default function InventoryCategoryEditor() {
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [filterCat, setFilterCat] = useState("Other");
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const [c, i] = await Promise.all([
        api.get("/inventory/categories"),
        api.get("/inventory?limit=50000"),
      ]);
      setCats(Array.isArray(c.data) ? c.data : []);
      setItems(Array.isArray(i.data) ? i.data : (Array.isArray(i.data?.items) ? i.data.items : []));
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not load inventory"));
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items
      .filter((it) => (it.category || "Other") === filterCat)
      .filter((it) => {
        if (!q) return true;
        return (
          (it.name || "").toLowerCase().includes(q) ||
          (it.sku || "").toLowerCase().includes(q) ||
          (it.manufacturer || "").toLowerCase().includes(q)
        );
      });
  }, [items, filterCat, search]);

  const allCatNames = useMemo(() => {
    const set = new Set(cats.map((c) => c.name));
    set.add("Pump"); set.add("Filter"); set.add("Membrane"); set.add("Tank");
    set.add("Chemical"); set.add("Resin / Media"); set.add("UV"); set.add("Ozone");
    set.add("Valve"); set.add("Fitting / Connector"); set.add("Hose / Tubing");
    set.add("Drive / Control Panel"); set.add("Sensor / Probe");
    set.add("Bracket / Mount"); set.add("Distributor"); set.add("Electrical");
    set.add("Spare Part / Kit"); set.add("System / Vessel");
    return Array.from(set).sort();
  }, [cats]);

  const recategorize = async (item, newCat) => {
    if (!newCat || newCat === item.category) return;
    setBusyId(item.id);
    setErr("");
    try {
      await api.patch(`/inventory/${item.id}/category`, { category: newCat });
      // Update locally so the list re-filters instantly
      setItems((prev) => prev.map((p) => p.id === item.id ? { ...p, category: newCat } : p));
      // Refresh counts in the background
      api.get("/inventory/categories").then(({ data }) => setCats(data || [])).catch(() => {});
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not update"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="inventory-cat-editor">
      <div className="flex items-center gap-3">
        <Link to="/tools" className="hv-btn hv-btn-ghost text-xs">
          <ChevronLeft className="w-3 h-3" /> Tools
        </Link>
        <div className="w-9 h-9 rounded-md bg-[#1FB6C1]/15 border border-[#1FB6C1]/40 flex items-center justify-center">
          <Layers className="w-4 h-4 text-[#1FB6C1]" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-bold">Catalogue category editor</h1>
          <p className="text-xs text-[#647087]">Re-bucket inventory items so the storefront shows them under the right group.</p>
        </div>
      </div>

      {err && (
        <div className="hv-card p-3 flex items-center gap-2 border border-rose-300/30 bg-rose-300/5 text-rose-200 text-xs">
          <AlertCircle className="w-3.5 h-3.5" /> {err}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] gap-3">
        {/* Sidebar — categories */}
        <aside className="hv-card p-2 space-y-1 max-h-[80vh] overflow-y-auto">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] px-2 py-1.5">Categories</div>
          {cats.map((c) => (
            <div
              key={c.name}
              className={`w-full px-2.5 py-1.5 rounded text-xs flex items-center justify-between gap-1 ${filterCat === c.name ? "bg-[#1FB6C1]/10 text-[#1FB6C1] border border-[#1FB6C1]/40" : "border border-transparent hover:bg-white/5 text-[#a0aabf]"}`}
            >
              <button
                type="button"
                onClick={() => setFilterCat(c.name)}
                className="flex-1 text-left truncate"
                data-testid={`cat-side-${c.name}`}
                title={`Show ${c.count} ${c.name} items here`}
              >
                <span className="truncate">{c.name}</span>
              </button>
              <span className="text-[10px] font-mono opacity-70">{c.count}</span>
              {/* Wave 26.9 — drill-in to Equipment list filtered by this category */}
              <Link
                to={`/equipment?tab=inventory&category=${encodeURIComponent(c.name)}`}
                onClick={(e) => e.stopPropagation()}
                className="opacity-50 hover:opacity-100 hover:text-[#1FB6C1] transition-colors"
                title={`Open ${c.name} in Equipment list`}
                data-testid={`cat-drill-${c.name}`}
                aria-label={`Open ${c.name} in equipment list`}
              >
                <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          ))}
        </aside>

        {/* Items */}
        <div className="space-y-3">
          <div className="hv-card p-3 flex items-center gap-3 flex-wrap">
            <div className="text-sm">
              <span className="font-bold text-white">{filterCat}</span>
              <span className="text-[#647087] ml-2">{visible.length} item{visible.length === 1 ? "" : "s"}</span>
            </div>
            <div className="relative flex-1 max-w-md">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#647087]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, SKU, manufacturer…"
                className="hv-input !pl-8 text-xs"
                data-testid="cat-editor-search"
              />
            </div>
            {/* Wave 26.9 — Open the current category in the full Equipment screen
                (procurement / detail / datasheets / suppliers all live there). */}
            <Link
              to={`/equipment?tab=inventory&category=${encodeURIComponent(filterCat)}`}
              className="hv-btn hv-btn-ghost text-xs"
              title="Open this category in Equipment (full procurement + suppliers + datasheets)"
              data-testid="cat-open-equipment"
            >
              Open in Equipment <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          <div className="hv-card !p-0 overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-xs text-[#647087]">Loading inventory…</div>
            ) : visible.length === 0 ? (
              <div className="p-8 text-center text-xs text-[#647087]">No items match.</div>
            ) : (
              <div className="divide-y divide-white/5">
                {visible.map((it) => (
                  <div
                    key={it.id}
                    className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-3 py-2.5 hover:bg-white/[0.02]"
                    data-testid={`cat-row-${it.id}`}
                  >
                    {/* Wave 26.10.7 — name+SKU row stacks above the dropdown
                        on mobile (<sm) so the description is always visible.
                        Side-by-side on tablet/desktop. */}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-white break-words">{it.name || "(no name)"}</div>
                      <div className="text-[10px] font-mono text-[#647087] truncate">
                        {it.sku ? `SKU ${it.sku}` : "No SKU"}{it.manufacturer ? ` · ${it.manufacturer}` : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <select
                        value={it.category || "Other"}
                        onChange={(e) => recategorize(it, e.target.value)}
                        disabled={busyId === it.id}
                        className="hv-input text-xs !h-8 w-full sm:w-auto sm:!min-w-[200px]"
                        data-testid={`cat-select-${it.id}`}
                      >
                        {allCatNames.map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                      {busyId === it.id && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#1FB6C1]" />}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
